import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

function Homepage() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    // Get company language from localStorage
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    // Listen for language updates
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    // Check periodically
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  return (
    <>
      <Helmet><title>Home - Galla Mandi Bills & Khata</title></Helmet>
      
      <div
        className={`${companyLanguage === 'ur' ? 'font-urdu' : ''} bg-brand-background`}
        dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
      >
        {/* Hero Section with Video Background */}
        <section className="relative text-white pt-8 pb-20 md:pt-12 md:pb-32 overflow-hidden">
          {/* Video Background */}
          <div className="absolute inset-0 w-full h-full z-0">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
                minHeight: '100%',
                minWidth: '100%',
                width: '100%',
                height: '100%'
              }}
            >
              <source src="/bg1.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Light overlay for text readability only */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center relative">
              {/* Green Box Container */}
              <div className="bg-brand-header/95 backdrop-blur-sm rounded-2xl px-6 py-5 md:px-8 md:py-6 shadow-2xl border-2 border-white/20 animate-fade-in-up inline-block">
                <p className="text-base md:text-lg text-white/90 mb-6 leading-relaxed" style={{ lineHeight: '1.8' }}>
                  {companyLanguage === 'ur'
                    ? 'گلہ منڈی کے کمیشن ایجنٹس کے لیے آسان آن لائن سسٹم'
                    : 'Simple online system for galla mandi commission agents'}
                </p>
                <h1 className="text-3xl md:text-3xl lg:text-4xl font-bold mb-6 leading-relaxed text-white" style={{ lineHeight: '1.8' }}>
                  {companyLanguage === 'ur'
                    ? 'بل، بِکری، ریکوری اور کھاتہ — سب کچھ ایک ہی جگہ'
                    : 'Bills, sales, recoveries & ledger — all in one place'}
                </h1>
                <p className="text-base md:text-lg text-white/90 mb-3 leading-relaxed" style={{ lineHeight: '1.8' }}>
                  {companyLanguage === 'ur'
                    ? 'اپنی گلہ منڈی کا روزنامچہ، بل اور پارٹی کھاتے کمپیوٹر اور موبائل پر محفوظ کریں، اردو اور انگریزی پرنٹ کے ساتھ۔'
                    : 'Manage your mandi roznamcha, bills and party ledgers on computer and mobile, with Urdu and English printing.'}
                </p>
                <div className="flex items-center justify-center gap-8 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-brand-button animate-soft-bounce">24/7</div>
                    <p className="text-sm text-white/80 mt-3">
                      {companyLanguage === 'ur'
                        ? 'جہاں بھی ہوں، اپنا کھاتہ آن لائن دیکھیں'
                        : 'Access your ledger anytime, anywhere'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <button className="bg-brand-button hover:bg-white/90 text-brand-header font-semibold px-8 py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg">
                    {companyLanguage === 'ur' ? 'کمپنی لاگ اِن کریں' : 'Login as Company'}
                  </button>
                  <Link
                    to="/company/login"
                    className="underline text-brand-button text-sm hover:text-white transition-colors"
                  >
                    {companyLanguage === 'ur'
                      ? 'نیا کھاتہ بنانے کے لیے یہاں کلک کریں'
                      : 'Click here to open your online account'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Choose the Best Section */}
        <section className="py-20 bg-brand-background">
          <div className="container mx-auto px-4">
            <div className={`text-center mb-16 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              <p className="text-brand-header font-semibold mb-2">
                {companyLanguage === 'ur' ? 'اپنی منڈی کا بہترین سسٹم منتخب کریں' : 'Choose the best system for your mandi'}
              </p>
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {companyLanguage === 'ur'
                  ? 'کمیشن ایجنٹس کے لیے خاص طور پر بنایا گیا آن لائن کھاتہ'
                  : 'Online billing & ledger built for commission agents'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-brand-card/70 p-8 rounded-xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-brand-header/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-3 text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'بل اور بِکری ایک کلک پر' : 'Bills & sales in one click'}
                </h3>
                <p className={`text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur'
                    ? 'گاہک کے نام، گاڑی نمبر، خرچے اور بِکری ایک modern فارم سے بھریں اور فوراً پرنٹ نکالیں۔'
                    : 'Fill customer name, vehicle number, expenses and sales in a modern form and print the bill instantly.'}
                </p>
              </div>
              <div className="bg-brand-card/70 p-8 rounded-xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-brand-header/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-3 text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'سیل، ریکوری اور کھاتہ کنٹرول' : 'Control sales, recoveries & ledger'}
                </h3>
                <p className={`text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur'
                    ? 'ہر پارٹی کا قابل وصول اور قابل ادائیگی بیلنس خود بخود گنیں، Company Dashboard سے مکمل overview دیکھیں۔'
                    : 'Automatically track receivable/payable balance per party and see full overview in the Company Dashboard.'}
                </p>
              </div>
              <div className="bg-brand-card/70 p-8 rounded-xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-brand-header/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-3 text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'اردو نستعلیق اور دو لسانی پرنٹنگ' : 'Urdu Nastaleeq & bilingual printing'}
                </h3>
                <p className={`text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur'
                    ? 'بل اور کھاتہ اردو نستعلیق میں بھی، اور English میں بھی — دونوں زبانوں میں صاف پرنٹ۔'
                    : 'Print bills and ledgers in beautiful Urdu Nastaleeq as well as English, exactly like your paper books.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section className="py-20 bg-brand-header text-white">
          <div className={`container mx-auto px-4 text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <div className="max-w-3xl mx-auto">
              <p className="text-brand-button font-semibold mb-2">
                {companyLanguage === 'ur' ? 'گلہ منڈی کا تجربہ، سافٹ ویئر کی آسانی' : 'Mandi experience, software simplicity'}
              </p>
              <h2 className="text-4xl font-bold mb-4">
                {companyLanguage === 'ur'
                  ? 'رجسٹر سے ڈیجیٹل کھاتے تک، ایک smooth سفر'
                  : 'From paper registers to digital ledgers, in a smooth journey'}
              </h2>
              <p className="text-white/90 mb-8">
                {companyLanguage === 'ur'
                  ? 'ہماری system میں بل، سیل، ریکوری، روزنامچہ اور پارٹی کھاتے وہی رہتے ہیں جو آپ پہلے ہاتھ سے لکھتے تھے — بس اب غلطی کم اور حساب تیز ہو جاتا ہے۔'
                  : 'Our system keeps your bills, sales, recoveries, roznamcha and party ledgers just like your paper books — only faster and with fewer mistakes.'}
              </p>
              <Link to="/company/login" className="bg-brand-button hover:bg-white/90 text-brand-header font-semibold px-8 py-3 rounded-lg transition inline-block transform hover:-translate-y-0.5 hover:shadow-lg">
                {companyLanguage === 'ur' ? 'اپنی کمپنی میں لاگ اِن کریں' : 'Login to your company'}
              </Link>
            </div>
          </div>
        </section>

        {/* Solutions Section */}
        <section className="py-20 bg-brand-background">
          <div className="container mx-auto px-4">
            <div className={`text-center mb-16 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {companyLanguage === 'ur' ? 'گلہ منڈی کے لیے practical فیچرز' : 'Practical features for galla mandi'}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(companyLanguage === 'ur' ? [
                { title: "بل مینجمنٹ", desc: "ہر گاہک کے لیے الگ serial number، گاڑی نمبر، خرچ اور بِکری کے ساتھ مکمل bill history۔" },
                { title: "سیل اور ریکوری فارم", desc: "سیل اور ریکوری الگ الگ screen سے ڈالیں، اور کھاتہ خود بخود update ہوتا رہے۔" },
                { title: "کسٹمر اور سپلائر کھاتہ", desc: "Customer Khata اور Supplier Khata میں ہر party کا موجودہ بیلنس واضح نظر آئے۔" },
                { title: "روزنامچہ / Roz Namacha", desc: "روزانہ کے سیل، ریکوری اور اخراجات کا خلاصہ ایک جگہ دیکھیں۔" },
                { title: "Multi User & Online Backup", desc: "آفس اور گودام دونوں سے ایک ہی کمپنی میں login کریں، data online محفوظ رہے۔" },
                { title: "Reports & Printing", desc: "اردو اور English دونوں میں reports اور bills کا صاف ستھرا پرنٹ حاصل کریں۔" }
              ] : [
                { title: "Bill Management", desc: "Keep complete bill history with serial number, vehicle number, expenses and sales per customer." },
                { title: "Sales & Recovery Forms", desc: "Enter sales and recoveries on separate screens while the ledger updates automatically." },
                { title: "Customer & Supplier Ledger", desc: "See clear receivable/payable balance for each party in Customer and Supplier Khata." },
                { title: "Daily Roznamcha", desc: "View daily summary of sales, recoveries and expenses in one place." },
                { title: "Multi User & Online Backup", desc: "Allow multiple users to log in to the same company with data safely stored online." },
                { title: "Reports & Printing", desc: "Get clean, professional printouts of bills and reports in both Urdu and English." }
              ]).map((service, idx) => (
                <div key={idx} className={`bg-brand-card/70 p-6 rounded-lg hover:shadow-xl transition transform hover:-translate-y-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  <h3 className={`text-xl font-bold mb-3 text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{service.title}</h3>
                  <p className={`text-gray-700 mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{service.desc}</p>
                  <Link to="/company/dashboard" className="text-brand-header font-semibold hover:text-brand-header/80">
                    {companyLanguage === 'ur' ? 'ڈیش بورڈ پر دیکھیں →' : 'See in dashboard →'}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
      <section className="pt-8 pb-20 bg-brand-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(companyLanguage === 'ur' ? [
              { title: "بل مینجمنٹ سسٹم", category: "بل" },
              { title: "روزنامچہ اور کھاتہ", category: "کھاتہ" },
              { title: "سیل اور ریکوری", category: "سیل" },
              { title: "بینک کھاتہ مینجمنٹ", category: "بینک" }
            ] : [
              { title: "Bill Management System", category: "Bill" },
              { title: "Roznamcha & Ledger", category: "Ledger" },
              { title: "Sales & Recovery", category: "Sale" },
              { title: "Bank Account Management", category: "Bank" }
            ]).map((project, idx) => (
              <div key={idx} className={`bg-white/90 p-6 rounded-xl shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-brand-card/60 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <div className={`text-3xl font-bold text-brand-header mb-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{project.category}</div>
                <h3 className={`text-lg font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{project.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* Testimonials Section */}
      <section className="py-20 bg-brand-background">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold text-brand-header mb-4">
              {companyLanguage === 'ur' ? 'گلہ منڈی کے صارفین کی رائے' : 'What Our Users Say'}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(companyLanguage === 'ur' ? [
              { name: "احمد علی", role: "کمیشن ایجنٹ", text: "یہ سسٹم واقعی بہت آسان ہے۔ اب میں اپنے تمام بل، کھاتے اور روزنامچہ ایک جگہ سے manage کر سکتا ہوں۔ اردو پرنٹ بھی بہت صاف آتا ہے۔" },
              { name: "محمد حسن", role: "منڈی ایجنٹ", text: "پہلے ہاتھ سے لکھنے میں بہت وقت لگتا تھا اور غلطیاں بھی ہوتی تھیں۔ اب سب کچھ آٹومیٹک ہے اور حساب بہت تیز ہو گیا ہے۔" },
              { name: "علی رضا", role: "گلہ منڈی کاروباری", text: "بینک کھاتہ، اخراجات اور ریکوری سب کچھ ایک ہی ڈیش بورڈ پر نظر آتا ہے۔ یہ سسٹم ہمارے کام کو بہت آسان بنا دیتا ہے۔" }
            ] : [
              { name: "Ahmed Ali", role: "Commission Agent", text: "This system is really easy to use. Now I can manage all my bills, ledgers and roznamcha from one place. The Urdu print is also very clean." },
              { name: "Muhammad Hassan", role: "Mandi Agent", text: "Writing by hand used to take a lot of time and mistakes happened. Now everything is automatic and calculations are much faster." },
              { name: "Ali Raza", role: "Galla Mandi Businessman", text: "Bank accounts, expenses and recoveries all show on the same dashboard. This system makes our work much easier." }
            ]).map((testimonial, idx) => (
              <div key={idx} className={`bg-white p-8 rounded-xl shadow-md border border-brand-card/70 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-brand-button text-xl">★</span>
                  ))}
                </div>
                <p className={`text-gray-700 mb-6 italic ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>"{testimonial.text}"</p>
                <div>
                  <p className={`font-bold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{testimonial.name}</p>
                  <p className={`text-gray-600 text-sm ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* FAQ Section */}
      <section className="py-20 bg-brand-background">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold text-brand-header mb-4">
              {companyLanguage === 'ur' ? 'اکثر پوچھے جانے والے سوالات' : 'Frequently Asked Questions'}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {(companyLanguage === 'ur' ? [
              { q: "یہ سسٹم کس کے لیے ہے؟", a: "یہ سسٹم گلہ منڈی کے کمیشن ایجنٹس، منڈی کاروباریوں اور ایسے لوگوں کے لیے بنایا گیا ہے جو اپنے بل، کھاتے اور روزنامچہ آن لائن manage کرنا چاہتے ہیں۔" },
              { q: "کیا میں اپنا کھاتہ آن لائن دیکھ سکتا ہوں؟", a: "جی ہاں! آپ کہیں بھی ہوں، کسی بھی وقت اپنے کھاتے، بل، سیل اور ریکوری کی تفصیلات آن لائن دیکھ سکتے ہیں۔" },
              { q: "کیا اردو میں پرنٹ لے سکتے ہیں؟", a: "جی ہاں! آپ اپنے تمام بل اور کھاتے اردو نستعلیق اور انگریزی دونوں زبانوں میں پرنٹ لے سکتے ہیں۔" },
              { q: "سیل اور ریکوری کیسے درج کریں؟", a: "سیل اور ریکوری کے لیے الگ الگ فارم ہیں۔ آپ انہیں Dashboard سے آسانی سے بھر سکتے ہیں اور کھاتہ خود بخود update ہو جائے گا۔" },
              { q: "بینک کھاتہ کیسے استعمال کریں؟", a: "بینک کھاتہ میں آپ اپنے تمام بینک اکاؤنٹس اور ادائیگی کے طریقوں کا حساب رکھ سکتے ہیں۔ ہر بینک کا الگ کھاتہ ہوتا ہے جہاں deposit اور withdrawal دیکھ سکتے ہیں۔" }
            ] : [
              { q: "Who is this system for?", a: "This system is designed for galla mandi commission agents, mandi businessmen and anyone who wants to manage their bills, ledgers and roznamcha online." },
              { q: "Can I view my ledger online?", a: "Yes! You can view your ledger, bills, sales and recovery details online anytime, anywhere." },
              { q: "Can I print in Urdu?", a: "Yes! You can print all your bills and ledgers in both Urdu Nastaleeq and English." },
              { q: "How to enter sales and recoveries?", a: "There are separate forms for sales and recoveries. You can easily fill them from the Dashboard and the ledger will update automatically." },
              { q: "How to use Bank Khata?", a: "In Bank Khata, you can keep track of all your bank accounts and payment methods. Each bank has a separate ledger where you can see deposits and withdrawals." }
            ]).map((faq, idx) => (
              <div key={idx} className={`bg-white p-6 rounded-lg mb-4 shadow-md border border-brand-card/60 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <h3 className={`font-bold text-brand-header mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>{faq.q}</h3>
                <p className={`text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-emerald-900 text-white">
           <div className={`container mx-auto px-4 text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-6">
              {companyLanguage === 'ur' ? 'اپنی گلہ منڈی کا کھاتہ آج ہی آن لائن لے آئیں۔' : 'Bring your galla mandi ledger online today.'}
            </h2>
            <Link to="/company/login" className="bg-brand-button hover:bg-white/90 text-brand-header font-semibold px-8 py-3 rounded-lg transition inline-block transform hover:-translate-y-0.5 hover:shadow-lg">
              {companyLanguage === 'ur' ? 'کمپنی لاگ اِن / رجسٹر' : 'Company Login / Register'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default Homepage;

